import * as XLSX from "xlsx";
import { isValid, parse } from "date-fns";
import { MeioPagamentoService, PessoaService } from "../../services";
import { formatPrecisaoFloat, formatPrice, unformatPrice } from "../../utils";

const pessoaService = new PessoaService();
const meioPagamentoService = new MeioPagamentoService();

const tamanhoLote = 500;

const colunasPlanilha = [
  { campo: "numeroTitulo", nomes: ["Nº Título"] },
  { campo: "origem", nomes: ["Origem"] },
  { campo: "dataLancamento", nomes: ["Data de Lançamento", "Data Lançamento"] },
  { campo: "dataVencimento", nomes: ["Data de Vencimento", "Data Vencimento"] },
  {
    campo: "dataCompetencia",
    nomes: ["Data de Competência", "Data Competência"],
    opcional: true,
  },
  { campo: "meioPagamentoId", nomes: ["Meio de Pagamento"] },
  { campo: "valor", nomes: ["Valor Bruto"] },
  { campo: "valorTotal", nomes: ["Valor Total"] },
  { campo: "historico", nomes: ["Histórico"] },
  { campo: "planoFinanceiroId", nomes: ["Plano Financeiro"], opcional: true },
  { campo: "centroCustoId", nomes: ["Centro de Custo"], opcional: true },
];

export const formatarData = ({ value }) => value?.toLocaleDateString("pt-BR");

const formatarValor = ({ value }) => formatPrice(value);

export const descricaoCadastro = (id, descricao) =>
  descricao ? `${id} - ${descricao}` : id;

export function colunasGrid(nomePessoa, colunasExtras) {
  return [
    { field: "linha", headerName: "Linha", width: 80 },
    { field: "numeroTitulo", headerName: "Nº Título", width: 150 },
    { field: "origem", headerName: "Origem", width: 150 },
    {
      field: "pessoaId",
      headerName: nomePessoa,
      width: 320,
      valueGetter: (params) =>
        descricaoCadastro(params.value, params.row.pessoaNome),
    },
    {
      field: "dataLancamento",
      headerName: "Data de Lançamento",
      width: 160,
      valueFormatter: formatarData,
    },
    {
      field: "dataVencimento",
      headerName: "Data de Vencimento",
      width: 160,
      valueFormatter: formatarData,
    },
    {
      field: "meioPagamentoId",
      headerName: "Meio de Pagamento",
      width: 250,
      valueGetter: (params) =>
        descricaoCadastro(params.value, params.row.meioPagamentoDescricao),
    },
    {
      field: "valor",
      headerName: "Valor Bruto",
      width: 140,
      valueFormatter: formatarValor,
    },
    {
      field: "acrescimo",
      headerName: "Acréscimo",
      width: 130,
      valueFormatter: formatarValor,
    },
    {
      field: "desconto",
      headerName: "Desconto",
      width: 130,
      valueFormatter: formatarValor,
    },
    {
      field: "valorTotal",
      headerName: "Valor Total",
      width: 140,
      valueFormatter: formatarValor,
    },
    ...colunasExtras,
    { field: "historico", headerName: "Histórico", width: 300 },
  ];
}

function normalizarCabecalho(texto) {
  return String(texto ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

export function converterTexto(valor) {
  const texto = String(valor ?? "").trim();
  return texto || null;
}

export function converterNumero(valor) {
  if (typeof valor === "number") return valor;
  const texto = converterTexto(valor);
  return texto ? Number(unformatPrice(texto)) : null;
}

export function converterData(valor) {
  const data =
    valor instanceof Date
      ? valor
      : parse(String(valor ?? "").trim(), "dd/MM/yyyy", new Date());
  return isValid(data) && data.getFullYear() >= 2000 ? data : null;
}

export function calcularValores(valorBruto, valorTotal) {
  const valor = valorBruto > 0 ? valorBruto : valorTotal;
  const diferenca =
    valorTotal > 0 ? formatPrecisaoFloat(valorTotal - valor) : 0;
  return {
    valor,
    valorTotal,
    acrescimo: diferenca > 0 ? diferenca : 0,
    desconto: diferenca < 0 ? -diferenca : 0,
  };
}

export function validarTitulo(titulo) {
  if (!titulo.numeroTitulo) {
    titulo.erros.numeroTitulo = "Nº Título não informado";
  }
  if (!titulo.dataLancamento) {
    titulo.erros.dataLancamento =
      "Data de Lançamento não informada ou inválida";
  }
  if (!titulo.dataVencimento) {
    titulo.erros.dataVencimento =
      "Data de Vencimento não informada ou inválida";
  }
  if (!(titulo.valorTotal > 0)) {
    titulo.erros.valorTotal = "Valor Total deve ser maior que zero";
  }
  if (titulo.historico?.length > 255) {
    titulo.erros.historico = "Histórico com mais de 255 caracteres";
  }
}

export async function buscarCadastro(requisicao, mensagemErro) {
  const result = await requisicao;
  if (result.isAxiosError) {
    throw new Error(result.response?.data?.message || mensagemErro);
  }
  return result.data;
}

export async function buscarPessoas(linhas) {
  const ids = [
    ...new Set(linhas.map(({ pessoaId }) => converterNumero(pessoaId))),
  ].filter((id) => id > 0);
  if (!ids.length) return [];
  return buscarCadastro(
    pessoaService.getAllFiltroAvancadoSimplificada({
      id: ids,
      nonPaginated: true,
      attributes: ["id", "nomeRazaoSocial"],
    }),
    "Erro ao buscar as pessoas da planilha",
  );
}

export function buscarMeiosPagamento() {
  return buscarCadastro(
    meioPagamentoService.getAll({ nonPaginated: true }),
    "Erro ao buscar os meios de pagamento",
  );
}

export async function lerPlanilha(arquivo, nomeColunaPessoa) {
  const conteudo = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error("Erro ao ler o arquivo CSV/XLS"));
    reader.readAsArrayBuffer(arquivo);
  });
  const workbook = XLSX.read(conteudo, {
    type: "buffer",
    cellDates: true,
    raw: true,
  });
  const planilha = workbook.Sheets[workbook.SheetNames[0]];
  const [cabecalhos = []] = XLSX.utils.sheet_to_json(planilha, { header: 1 });
  const colunas = [
    { campo: "pessoaId", nomes: [nomeColunaPessoa] },
    ...colunasPlanilha,
  ].map((coluna) => ({
    ...coluna,
    cabecalho: cabecalhos.find((cabecalho) =>
      coluna.nomes.some((nome) =>
        normalizarCabecalho(cabecalho).startsWith(normalizarCabecalho(nome)),
      ),
    ),
  }));
  const colunasFaltando = colunas
    .filter(({ cabecalho, opcional }) => !cabecalho && !opcional)
    .map(({ nomes }) => nomes[0]);
  if (colunasFaltando.length) {
    throw new Error(
      `Colunas não encontradas na planilha: ${colunasFaltando.join(", ")}`,
    );
  }
  const linhas = XLSX.utils.sheet_to_json(planilha).map((linhaPlanilha) => {
    const linha = { linha: linhaPlanilha.__rowNum__ + 1 };
    colunas.forEach(({ campo, cabecalho }) => {
      linha[campo] = linhaPlanilha[cabecalho];
    });
    return linha;
  });
  if (!linhas.length) {
    throw new Error("A planilha não possui títulos para importar");
  }
  return linhas;
}

export function marcarTitulosRepetidos(titulos) {
  const linhasPorTitulo = {};
  titulos.forEach((titulo) => {
    const chave = `${titulo.numeroTitulo}|${titulo.pessoaId}`;
    if (linhasPorTitulo[chave]) {
      titulo.erros.numeroTitulo = `Título repetido na planilha (linha ${linhasPorTitulo[chave]})`;
    } else {
      linhasPorTitulo[chave] = titulo.linha;
    }
  });
}

export function validarTitulosComErro(titulos) {
  const titulosComErro = titulos.filter(
    (titulo) => Object.keys(titulo.erros).length,
  );
  if (titulosComErro.length) {
    throw new Error(
      `Existem ${titulosComErro.length} títulos com erro. Corrija-os e selecione o arquivo novamente`,
    );
  }
}

export async function enviarEmLotes(titulos, service) {
  const resultado = { importados: 0, ignorados: 0 };
  for (let inicio = 0; inicio < titulos.length; inicio += tamanhoLote) {
    const lote = titulos
      .slice(inicio, inicio + tamanhoLote)
      .map((titulo) => titulo.dadosImportacao());
    const res = await service.importar(lote);
    if (res.isAxiosError) {
      const mensagem =
        res.response?.data?.message || "Erro ao importar os títulos";
      throw new Error(
        resultado.importados
          ? `${mensagem}. ${resultado.importados} títulos foram importados antes do erro; ao importar a planilha novamente, eles serão ignorados`
          : mensagem,
      );
    }
    resultado.importados += res.data.importados;
    resultado.ignorados += res.data.ignorados;
  }
  return resultado;
}

export function mensagemResultado({ importados, ignorados }) {
  return ignorados
    ? `${importados} títulos importados. ${ignorados} já estavam cadastrados e foram ignorados`
    : `${importados} títulos importados com sucesso`;
}
